// frontend/src/components/SignedAudio.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, IconButton, Tooltip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useSubscription } from '../hooks/useSubscription';

const SignedAudio = ({ audioUrl, projectId, noteId, ...audioProps }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const { currentUser } = useAuth();
  const { currentTier } = useSubscription();
  const canDownload = currentTier() !== 'free';

  useEffect(() => {
    if (!currentUser) return;

    // Require either audioUrl (legacy) OR projectId+noteId (new S3-first)
    if (!audioUrl && !projectId && !noteId) return;

    const generateSignedUrl = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get auth token
        const token = await currentUser.getIdToken();

        let response;

        // NEW: Projects V2 S3-first playback endpoint
        if (projectId && noteId) {
          // Use new S3-first endpoint with 302 redirect
          response = await fetch(`/api/v2/projects/${projectId}/voices/${noteId}/audio`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            throw new Error(`Failed to load audio: ${response.statusText}`);
          }

          // The backend will 302 redirect to S3 or stream GridFS
          // Use the final URL after redirects
          setSignedUrl(response.url);

        } else {
          // LEGACY: Old audioUrl pattern with /api/files/
          const fileId = audioUrl.replace('/api/files/', '');

          // Request signed URL
          response = await fetch(`/api/files/${fileId}/signed`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            throw new Error(`Failed to generate signed URL: ${response.statusText}`);
          }

          const { signedUrl: url } = await response.json();
          setSignedUrl(url);
        }
      } catch (err) {
        console.error('Error generating signed audio URL:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    generateSignedUrl();
  }, [audioUrl, projectId, noteId, currentUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
        <CircularProgress size={16} />
        <span>Loading audio...</span>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ color: 'error.main', p: 1, fontSize: '0.875rem' }}>
        Error loading audio: {error}
      </Box>
    );
  }

  if (!signedUrl) return null;

  const handleDownload = async () => {
    if (!canDownload || !noteId) {
      alert('Downloads are a paid feature. Upgrade to Starter for 10 GB/month downloads.');
      return;
    }

    try {
      setDownloading(true);
      const token = await currentUser.getIdToken();

      // Use /api/audio/:id/download endpoint (with egress tracking)
      const downloadUrl = `/api/audio/${noteId}/download?format=webm`;

      // Fetch the file
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429 && errorData.error === 'egress_limit_exceeded') {
          alert(errorData.message || 'Download limit exceeded. Please upgrade for more bandwidth.');
        } else {
          throw new Error(`Download failed: ${response.statusText}`);
        }
        return;
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `voice-${noteId}.webm`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      alert(`Failed to download: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <audio
        controls
        src={signedUrl}
        {...audioProps}
        style={{ flexGrow: 1 }}
      >
        Your browser does not support the audio element.
      </audio>
      <Tooltip title={!canDownload ? "Downloads are a paid feature. Upgrade to Starter (10 GB/mo)." : "Download audio file"}>
        <span>
          <IconButton
            onClick={handleDownload}
            disabled={!canDownload || downloading}
            size="small"
            color="primary"
          >
            {downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default SignedAudio;